import type { DiscoverGamePatchRequest, GameStatus } from "libs/apiClient";
import { DiscoverGameInlinePatchField } from "../../../datagrid-configs/discoverGames";
import type { BulkEligibility, DiscoverGamesBulkUpdatePayload } from "./DiscoverGamesBulkUpdateModal";

/**
 * Build a per-row PATCH body that only contains the fields the row is eligible
 * for. Returns `null` when no eligible field remains (row is fully skipped).
 */
export function buildBulkPatchRequestForId(
	gameId: number,
	payload: DiscoverGamesBulkUpdatePayload,
	eligibility: BulkEligibility,
): DiscoverGamePatchRequest | null {
	const requestedFields: DiscoverGameInlinePatchField[] = [];
	const body: DiscoverGamePatchRequest = { id: gameId, requestedFields };

	if (payload.applyClubManager) {
		requestedFields.push(DiscoverGameInlinePatchField.ClubManagerId);
		body.clubManagerId = payload.clubManagerId;
	}
	if (payload.applyContentManager) {
		requestedFields.push(DiscoverGameInlinePatchField.ContentManagerId);
		body.contentManagerId = payload.contentManagerId;
	}
	if (payload.applyAssignee && eligibility.assignee.eligible.has(gameId)) {
		requestedFields.push(DiscoverGameInlinePatchField.AssigneeId);
		body.assigneeId = payload.assigneeId;
	}
	if (payload.applyPriority && eligibility.priority.eligible.has(gameId)) {
		requestedFields.push(DiscoverGameInlinePatchField.Priority);
		body.priority = payload.priority;
	}
	if (payload.applyGameStatus && eligibility.gameStatus.eligible.has(gameId)) {
		requestedFields.push(DiscoverGameInlinePatchField.GameStatus);
		body.gameStatus = payload.gameStatus as GameStatus;
	}

	return requestedFields.length > 0 ? body : null;
}

/** Mirror `buildBulkPatchRequestForId` for skip-confirm counts in the bulk modal. */
export function countBulkApplyPreview(
	selectedIds: Iterable<number>,
	selectedCount: number,
	payload: DiscoverGamesBulkUpdatePayload,
	eligibility: BulkEligibility,
): { applyCount: number; skippedCount: number } {
	if (!eligibility.hasData) {
		return { applyCount: selectedCount, skippedCount: 0 };
	}
	let applyCount = 0;
	for (const id of selectedIds) {
		if (buildBulkPatchRequestForId(id, payload, eligibility) !== null) {
			applyCount += 1;
		}
	}
	return {
		applyCount,
		skippedCount: Math.max(selectedCount - applyCount, 0),
	};
}
